import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useTheme } from '../../lib/theme-provider';

interface RTIChartData {
  date: string;
  score: number;
  state: string;
}

interface RTILineChartProps {
  data: RTIChartData[];
}

function zoneColor(score: number): string {
  if (score <= 35) return '#22c55e';
  if (score <= 60) return '#eab308';
  return '#ef4444';
}

export function RTILineChart({ data }: RTILineChartProps) {
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';

  const gridStroke = isDark ? 'rgba(148, 163, 184, 0.35)' : '#e5e5e5';
  const axisStroke = isDark ? '#94a3b8' : '#737373';
  const tickFill = isDark ? '#cbd5e1' : '#525252';
  const tooltipBg = isDark ? '#1e293b' : '#ffffff';
  const tooltipBorder = isDark ? '#475569' : '#e5e5e5';
  const tooltipLabel = isDark ? '#f1f5f9' : '#171717';

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="flex h-80 items-center justify-center text-neutral-500 dark:text-neutral-400">
        No chart data available
      </div>
    );
  }

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
          <defs>
            <linearGradient id="rtiLineGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#a855f7" />
              <stop offset="50%" stopColor="#d946ef" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical />
          <XAxis dataKey="date" stroke={axisStroke} tick={{ fill: tickFill, fontSize: 12 }} />
          <YAxis
            stroke={axisStroke}
            tick={{ fill: tickFill, fontSize: 12 }}
            domain={[0, 100]}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: '8px',
              color: tooltipLabel,
            }}
            labelStyle={{ color: tooltipLabel }}
          />
          <ReferenceLine
            key="reference-stable"
            y={35}
            stroke="#22c55e"
            strokeDasharray="4 4"
            strokeOpacity={0.9}
            label={{ value: 'Stable', fill: '#4ade80', fontSize: 11 }}
          />
          <ReferenceLine
            key="reference-elastic"
            y={60}
            stroke="#eab308"
            strokeDasharray="4 4"
            strokeOpacity={0.9}
            label={{ value: 'Elastic', fill: '#facc15', fontSize: 11 }}
          />
          <Line
            key="line-score"
            type="monotone"
            dataKey="score"
            stroke="url(#rtiLineGradient)"
            strokeWidth={3}
            dot={(props: {
              cx?: number;
              cy?: number;
              index?: number;
              payload?: RTIChartData;
            }) => {
              const { cx, cy, payload } = props;
              if (cx == null || cy == null || !payload) return null;
              const fill = zoneColor(payload.score);
              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={5}
                  fill={fill}
                  stroke={isDark ? '#0f172a' : '#ffffff'}
                  strokeWidth={2}
                />
              );
            }}
            activeDot={{ r: 7, stroke: '#fff', strokeWidth: 2, fill: '#d946ef' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
